import React from 'react';
import { Button, Card, Divider, Text, Title, Badge, Accordion, Group } from '@mantine/core';
import { ArrowLeft, Check, Info, Bookmark } from 'lucide-react';
import { radarAxes, protocolAxes } from '../data/protocolData';
import { findCompatibleCombinations, generateProtocolName } from '../utils/buildEngine';
import { ProtocolRadarChart } from './ProtocolRadarChart';
interface BuildResultsProps {
  selectedOutcomes: string[];
  onReset: () => void;
}
export function BuildResults({
  selectedOutcomes,
  onReset
}: BuildResultsProps) {
  // Get compatible combinations from the buildEngine
  const compatibleCombinations = findCompatibleCombinations(selectedOutcomes);
  // Calculate the total number of possible combinations
  const totalPossibleCombinations = protocolAxes.reduce((total, axis) => {
    // Count how many options are available for this axis in the compatible combinations
    const uniqueOptionsForAxis = new Set(compatibleCombinations.map(combo => combo[axis.id])).size;
    // Multiply the running total by the number of options for this axis
    return total * (uniqueOptionsForAxis > 0 ? uniqueOptionsForAxis : 1);
  }, 1);
  // Get readable names for the selected outcomes
  const outcomeNames = selectedOutcomes.map(id => {
    const outcome = radarAxes.find(axis => axis.id === id);
    return outcome?.label || id;
  });
  // Handler to save the build to localStorage
  const handleSaveBuild = () => {
    // Generate a unique ID for the saved build
    const id = `build-${Date.now()}`;
    // Create the saved build object
    const savedBuild = {
      id,
      name: 'Optimized Build',
      selectedOutcomes: outcomeNames,
      combinations: compatibleCombinations.slice(0, 3).map(combo => ({
        ...combo,
        name: generateProtocolName(combo)
      })),
      savedAt: new Date().toISOString()
    };
    // Get existing saved builds from localStorage
    const existingSavedBuilds = localStorage.getItem('nutrivector-saved-builds');
    let savedBuilds = [];
    if (existingSavedBuilds) {
      savedBuilds = JSON.parse(existingSavedBuilds);
    }
    // Add the new build to the array
    savedBuilds.push(savedBuild);
    // Save back to localStorage
    localStorage.setItem('nutrivector-saved-builds', JSON.stringify(savedBuilds));
    // Show a confirmation to the user
    alert('Build saved successfully!');
  };
  return <div data-id="element-44">
      <Card shadow="sm" p="xl" radius="md" className="bg-white mb-8" data-id="element-45">
        <div className="text-center mb-6" data-id="element-46">
          <Badge size="lg" color="teal" variant="light" className="mb-4" data-id="element-47">
            Your Compatible Nutrition Plans
          </Badge>
          <Title order={2} className="text-3xl font-bold text-slate-800 mb-2" data-id="element-48">
            Optimized for Your Priorities
          </Title>
          <Group position="center" className="mb-4" data-id="element-49">
            {outcomeNames.map((name, index) => <Badge key={index} size="lg" color="emerald" variant="filled" className="bg-emerald-600" data-id="element-50">
                {name}
              </Badge>)}
          </Group>
          <Text className="text-slate-600" data-id="element-51">
            We've identified {totalPossibleCombinations} nutrition combinations
            that score highly for your selected health outcomes.
          </Text>
        </div>
        <Divider my="md" data-id="element-52" />
        <Accordion data-id="element-53">
          {protocolAxes.map(axis => <Accordion.Item key={axis.id} value={axis.id} data-id="element-54">
              <Accordion.Control icon={<Info size={16} data-id="element-56" />} data-id="element-55">
                <Text fw={500} className="uppercase text-xs tracking-wider" data-id="element-57">
                  {axis.label} Options
                </Text>
              </Accordion.Control>
              <Accordion.Panel data-id="element-58">
                <div className="space-y-4" data-id="element-59">
                  {axis.options.filter(option => compatibleCombinations.some(combo => combo[axis.id] === option.value)).map(option => <div key={option.value} className="border border-slate-200 rounded-md p-3" data-id="element-60">
                        <Group position="apart" className="mb-1" data-id="element-61">
                          <Text fw={600} className="text-slate-800" data-id="element-62">
                            {option.label}
                          </Text>
                          <Badge size="sm" color="emerald" variant="light" data-id="element-63">
                            Recommended
                          </Badge>
                        </Group>
                        <Text size="sm" className="text-slate-600" data-id="element-64">
                          {option.description}
                        </Text>
                      </div>)}
                  {!axis.options.some(option => compatibleCombinations.some(combo => combo[axis.id] === option.value)) && <Text className="text-slate-500 italic" data-id="element-65">
                      All options in this category can work with your
                      priorities.
                    </Text>}
                </div>
              </Accordion.Panel>
            </Accordion.Item>)}
        </Accordion>
        <Divider my="md" data-id="element-66" />
        <div className="bg-slate-50 p-4 rounded-md mb-6" data-id="element-67">
          <Title order={4} className="mb-2 text-slate-700" data-id="element-68">
            Top 3 Complete Combinations
          </Title>
          <Text size="sm" className="text-slate-600 mb-4" data-id="element-69">
            These full protocol combinations score highest for your selected
            priorities while providing diverse approaches.
          </Text>
          {compatibleCombinations.slice(0, 3).map((combo, index) => {
          const protocolName = generateProtocolName(combo);
          const [namePart1, namePart2] = protocolName.split(' | ');
          return <div key={index} className="bg-white p-6 rounded-md shadow-sm mb-6" data-id="element-70">
                <Group position="apart" className="mb-3" data-id="element-71">
                  <Badge size="md" color="emerald" variant="filled" className="bg-emerald-600" data-id="element-72">
                    Combination #{index + 1}
                  </Badge>
                  <Text size="sm" className="text-slate-500" data-id="element-73">
                    Compatibility Score: {combo._score.toFixed(1)}/5
                  </Text>
                </Group>
                <div className="text-center mb-4" data-id="element-74">
                  <Title order={4} className="text-slate-800" data-id="element-75">
                    {namePart1}
                  </Title>
                  <Text className="text-emerald-600 font-medium" data-id="element-76">
                    {namePart2}
                  </Text>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-77">
                  {/* Column 1: Protocol details */}
                  <div className="space-y-2" data-id="element-78">
                    <Text fw={500} className="text-slate-700 mb-2" data-id="element-79">
                      Protocol Details
                    </Text>
                    {Object.entries(combo).filter(([key]) => key !== '_score' && key !== 'radarData').map(([key, value]) => {
                  const axis = protocolAxes.find(a => a.id === key);
                  const option = axis?.options.find(o => o.value === value);
                  return <div key={key} className="flex items-center" data-id="element-80">
                            <Check size={14} className="text-emerald-500 mr-1 flex-shrink-0" data-id="element-81" />
                            <Text size="sm" className="text-slate-700" data-id="element-82">
                              <span className="font-medium" data-id="element-83">
                                {axis?.shortLabel || key}:
                              </span>{' '}
                              {option?.label || value}
                            </Text>
                          </div>;
                })}
                  </div>
                  {/* Column 2: Protocol benefits */}
                  <div data-id="element-84">
                    <Text fw={500} className="text-slate-700 mb-2" data-id="element-85">
                      Key Benefits
                    </Text>
                    <ul className="list-disc pl-5 space-y-1" data-id="element-86">
                      {selectedOutcomes.map((outcome, i) => {
                    const outcomeLabel = radarAxes.find(axis => axis.id === outcome)?.label;
                    return <li key={i} data-id="element-87">
                            <Text size="sm" className="text-slate-600" data-id="element-88">
                              Optimized for{' '}
                              <span className="font-medium text-emerald-600" data-id="element-89">
                                {outcomeLabel}
                              </span>
                            </Text>
                          </li>;
                  })}
                      <li data-id="element-90">
                        <Text size="sm" className="text-slate-600" data-id="element-91">
                          Compatible with your health priorities
                        </Text>
                      </li>
                      <li data-id="element-92">
                        <Text size="sm" className="text-slate-600" data-id="element-93">
                          Balanced approach to nutrition timing
                        </Text>
                      </li>
                    </ul>
                  </div>
                  {/* Column 3: Radar chart */}
                  <div data-id="element-94">
                    <Text fw={500} className="text-slate-700 mb-2 text-center" data-id="element-95">
                      Performance Profile
                    </Text>
                    <div className="h-64" data-id="element-96">
                      <ProtocolRadarChart data={combo.radarData} height={250} data-id="element-97" />
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
        <Group justify="center" mt="xl" data-id="element-98">
          <Button variant="light" leftSection={<ArrowLeft size={16} data-id="element-100" />} onClick={onReset} data-id="element-99">
            Start Over
          </Button>
          <Button variant="filled" leftSection={<Bookmark size={16} data-id="element-102" />} onClick={handleSaveBuild} className="bg-emerald-600 hover:bg-emerald-700" data-id="element-101">
            Save Build
          </Button>
        </Group>
      </Card>
    </div>;
}